import { computed, onUnmounted, ref, unref, watch } from "vue";
import {
  buildSongLengthMap,
  computeRemainingSetTime,
  countFittingSongs,
  resolveSetEndIndex
} from "@/utils/setlistCalculator";

/**
 * Live "songs that still fit" ticker for the set-end line.
 *
 * Reads the set clock on an interval and whenever `source` changes, but only
 * writes liveSlots / liveStatus when the value actually changes so a 1s tick
 * does not rebuild ranked lists that depend on these refs.
 *
 * `source` is a computed/ref (or a getter) of:
 *   { setDurationMinutes, setStartedAt, rankedSongs, upNextSong, currentSong,
 *     currentSongStartedAt, pauseTimeSec, defaultSongTimeSec, songLengthMap }
 *
 * The ticker itself is mirrored in web/admin/src/composables/useSetEndSlots.js.
 * useLiveSetEndLine below is concerts-only (admin adds the pin controls itself).
 */
function useSetEndSlots(source, { intervalMs = 1000 } = {}) {
  const liveSlots = ref(null);
  const liveStatus = ref("inactive");
  let timer = null;

  const readSource = () => {
    const value = unref(source);
    return typeof value === "function" ? value() : value;
  };

  const assign = (slots, status) => {
    if (slots !== liveSlots.value) liveSlots.value = slots;
    if (status !== liveStatus.value) liveStatus.value = status;
  };

  const tick = () => {
    const s = readSource() || {};
    if (!s.setDurationMinutes || s.setDurationMinutes <= 0 || !s.setStartedAt) {
      assign(null, "inactive");
      return;
    }
    const remainingSec = computeRemainingSetTime(s.setDurationMinutes, s.setStartedAt);
    const { slots, status } = countFittingSongs({
      remainingSec,
      rankedSongs: s.rankedSongs ?? [],
      upNextSong: s.upNextSong ?? null,
      pauseTimeSec: s.pauseTimeSec ?? 30,
      defaultSongTimeSec: s.defaultSongTimeSec ?? 240,
      songLengthMap: s.songLengthMap ?? {},
      currentSong: s.currentSong ?? null,
      currentSongStartedAt: s.currentSongStartedAt ?? null
    });
    assign(status === "inactive" ? null : slots, status);
  };

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = () => {
    stop();
    tick();
    timer = setInterval(tick, intervalMs);
  };

  watch(source, () => tick());
  onUnmounted(stop);

  return { liveSlots, liveStatus, start, stop };
}

/**
 * Shared set-end divider for the concerts and LED views.
 *
 * `input` is a computed/ref of:
 *   { event, rankedSongs, upNextSong, currentSong, pauseTimeSec,
 *     defaultSongTimeSec, settingsReady }
 * A null persisted count follows the timer. An expired unpinned set lands at
 * index 0 so the divider stays visible above the ranked list.
 */
export function useLiveSetEndLine(input, { intervalMs = 5000 } = {}) {
  const source = computed(() => {
    const raw = unref(input) || {};
    const event = raw.event;
    const durations = Array.isArray(event?.setlistDurations) ? event.setlistDurations : [];
    const setIdx = event?.currentSetIndex || 0;
    const duration = setIdx < durations.length ? durations[setIdx] : 0;
    const ranked = raw.rankedSongs ?? [];
    const upNext = raw.upNextSong ?? null;
    const playing = raw.currentSong ?? null;
    return {
      setDurationMinutes: event?.setlistEnabled && event?.currentSetStartedAt ? duration : 0,
      setStartedAt: event?.currentSetStartedAt ?? null,
      rankedSongs: ranked,
      upNextSong: upNext,
      currentSong: playing,
      currentSongStartedAt: playing?.performedAt ?? null,
      pauseTimeSec: raw.pauseTimeSec ?? 30,
      defaultSongTimeSec: raw.defaultSongTimeSec ?? 240,
      songLengthMap: buildSongLengthMap([
        ...ranked,
        ...(upNext ? [upNext] : []),
        ...(playing ? [playing] : [])
      ])
    };
  });

  const { liveSlots, liveStatus, start, stop } = useSetEndSlots(source, { intervalMs });

  const lineIndex = computed(() => {
    const raw = unref(input) || {};
    const event = raw.event;
    const ranked = raw.rankedSongs ?? [];
    const upNext = raw.upNextSong ?? null;
    if (!event?.setlistEnabled || !event?.currentSetStartedAt || !raw.settingsReady) return -1;
    const durations = Array.isArray(event.setlistDurations) ? event.setlistDurations : [];
    const setIdx = event.currentSetIndex || 0;
    if (setIdx >= durations.length) return -1;

    const fallbackIndex =
      liveSlots.value == null
        ? -1
        : resolveSetEndIndex({
            setEndSongCount: liveSlots.value,
            upNextSong: upNext,
            rankedSongs: ranked,
            fallbackIndex: -1
          });

    return resolveSetEndIndex({
      setEndSongCount: event.setEndSongCount,
      upNextSong: upNext,
      rankedSongs: ranked,
      fallbackIndex
    });
  });

  return { lineIndex, liveSlots, liveStatus, start, stop };
}
